<template>
  <div>
    <b-alert
      v-if="errorText"
      show
      variant="danger"
    >
      <div class="alert-body">
        {{ errorText }}
      </div>
    </b-alert>

    <b-spinner
      v-if="loading && !card"
      class="d-block mx-auto my-3"
    />

    <div v-if="card">
      <b-card>
        <div class="d-flex justify-content-between align-items-start flex-wrap">
          <div>
            <h4 class="mb-50">
              Платёж #{{ payment.id }}
              <b-badge
                :variant="statusVariant(payment.status)"
                class="ml-50"
              >
                {{ payment.status || '—' }}
              </b-badge>
            </h4>
            <p class="text-muted mb-0">
              {{ typeLabels[payment.type] || payment.type }} · {{ formatMoney(payment.amountKopecks) }}
              <span v-if="payment.walletAmountKopecks">+ кошелёк {{ formatMoney(payment.walletAmountKopecks) }}</span>
            </p>
          </div>
          <div class="mt-50">
            <b-button
              variant="outline-secondary"
              size="sm"
              class="mr-1"
              :disabled="loading"
              @click="load(false)"
            >
              Обновить
            </b-button>
            <b-button
              variant="primary"
              size="sm"
              :disabled="loading"
              @click="load(true)"
            >
              Спросить ЮKassa сейчас
            </b-button>
          </div>
        </div>
      </b-card>

      <b-alert
        v-if="applyResult"
        show
        :variant="applyResult.outcome === 'applied' ? 'success' : 'danger'"
        dismissible
        @dismissed="applyResult = null"
      >
        <div class="alert-body">
          <span v-if="applyResult.outcome === 'applied'">Платёж применён.</span>
          <span v-else-if="applyResult.outcome === 'failed'">Не удалось применить: {{ applyResult.error }}</span>
          <span v-else>Платёж не был применён обработчиком; подробности — в журнале ниже.</span>
        </div>
      </b-alert>

      <b-card title="Диагноз">
        <payment-findings
          :findings="card.findings"
          :busy="acting"
          @action="onAction"
        />
      </b-card>

      <b-row>
        <b-col lg="6">
          <b-card title="Платёж">
            <dl class="row mb-0">
              <dt class="col-sm-5">
                ЮKassa
              </dt>
              <dd class="col-sm-7">
                <code>{{ payment.externalOrderId }}</code>
              </dd>
              <dt class="col-sm-5">
                Номер заказа
              </dt>
              <dd class="col-sm-7">
                <code>{{ payment.internalOrderId }}</code>
              </dd>
              <dt class="col-sm-5">
                Плательщик
              </dt>
              <dd class="col-sm-7">
                {{ payment.payerId || '—' }}
              </dd>
              <dt class="col-sm-5">
                Создан
              </dt>
              <dd class="col-sm-7">
                {{ formatDate(payment.createdAt) }}
              </dd>
              <dt class="col-sm-5">
                Последнее уведомление
              </dt>
              <dd class="col-sm-7">
                {{ formatDate(payment.lastEventAt) }}
              </dd>
              <dt class="col-sm-5">
                Применён
              </dt>
              <dd class="col-sm-7">
                {{ formatDate(payment.processedAt) }}
              </dd>
              <dt class="col-sm-5">
                Обрабатывается с
              </dt>
              <dd class="col-sm-7">
                {{ formatDate(payment.processingAt) }}
              </dd>
            </dl>
          </b-card>
        </b-col>

        <b-col lg="6">
          <b-card title="За что платёж">
            <p
              v-if="!card.entity"
              class="text-muted mb-0"
            >
              Не привязан ни к регистрации, ни к заказу, ни к донату.
            </p>
            <div v-else>
              <p class="mb-50">
                <strong>{{ entityLabels[card.entity.type] || card.entity.type }} #{{ card.entity.id }}</strong>
                <b-badge
                  v-if="!card.entity.found"
                  variant="danger"
                  class="ml-50"
                >
                  не найден
                </b-badge>
                <b-badge
                  v-else-if="card.entity.paid === true"
                  variant="success"
                  class="ml-50"
                >
                  оплачен
                </b-badge>
                <b-badge
                  v-else-if="card.entity.paid === false"
                  variant="warning"
                  class="ml-50"
                >
                  не оплачен
                </b-badge>
                <b-badge
                  v-if="card.entity.found && card.entity.isCurrentAttempt === false"
                  variant="warning"
                  class="ml-50"
                >
                  номер заказа уже от другой попытки
                </b-badge>
              </p>
              <dl
                v-if="card.entity.details"
                class="row mb-0"
              >
                <template v-for="(value, key) in card.entity.details">
                  <dt
                    :key="`k-${key}`"
                    class="col-sm-5 text-muted"
                  >
                    {{ key }}
                  </dt>
                  <dd
                    :key="`v-${key}`"
                    class="col-sm-7"
                  >
                    {{ value === null || value === '' ? '—' : value }}
                  </dd>
                </template>
              </dl>
              <div
                v-if="card.entity.participants && card.entity.participants.length"
                class="mt-1"
              >
                <strong>Участники</strong>
                <ul class="mb-0">
                  <li
                    v-for="member in card.entity.participants"
                    :key="member.id"
                  >
                    {{ member.surname }} {{ member.name }}
                    <small class="text-muted">{{ member.email }}</small>
                  </li>
                </ul>
              </div>
            </div>
          </b-card>
        </b-col>
      </b-row>

      <b-card
        v-if="card.provider"
        title="Что говорит ЮKassa сейчас"
      >
        <p
          v-if="card.provider.error"
          class="text-danger mb-0"
        >
          Не удалось получить: {{ card.provider.error }}
        </p>
        <div v-else>
          <p class="mb-50">
            Статус:
            <b-badge :variant="statusVariant(card.provider.status)">
              {{ card.provider.status }}
            </b-badge>
            · оплачен: {{ card.provider.paid ? 'да' : 'нет' }}
            <span v-if="card.provider.amount">· сумма {{ card.provider.amount.value }} {{ card.provider.amount.currency }}</span>
          </p>
          <p class="mb-50 text-muted">
            Создан: {{ formatDate(card.provider.createdAt) }}
          </p>
          <div v-if="card.provider.refunds && card.provider.refunds.length">
            <strong>Возвраты</strong>
            <ul class="mb-0">
              <li
                v-for="refund in card.provider.refunds"
                :key="refund.id"
              >
                {{ refund.amount && refund.amount.value }} {{ refund.amount && refund.amount.currency }} —
                {{ refund.status }}, {{ formatDate(refund.createdAt) }}
              </li>
            </ul>
          </div>
        </div>
      </b-card>

      <b-card
        v-if="card.attempts.length > 1"
        title="Все попытки оплатить это"
      >
        <b-table
          :items="card.attempts"
          :fields="attemptFields"
          small
          responsive
          :tbody-tr-class="attemptClass"
        >
          <template #cell(id)="{ item }">
            <router-link
              v-if="!item.isThis"
              :to="{ name: 'payment-details', params: { id: item.id } }"
            >
              #{{ item.id }}
            </router-link>
            <strong v-else>#{{ item.id }} (этот)</strong>
          </template>
          <template #cell(status)="{ item }">
            <b-badge :variant="statusVariant(item.status)">
              {{ item.status || '—' }}
            </b-badge>
          </template>
          <template #cell(amountKopecks)="{ item }">
            {{ formatMoney(item.amountKopecks) }}
          </template>
          <template #cell(createdAt)="{ item }">
            {{ formatDate(item.createdAt) }}
          </template>
          <template #cell(processedAt)="{ item }">
            {{ item.processedAt ? formatDate(item.processedAt) : '—' }}
          </template>
          <template #cell(superseded)="{ item }">
            {{ item.superseded ? 'после неё была новая попытка' : 'последняя' }}
          </template>
        </b-table>
      </b-card>

      <b-row v-if="card.wallet || card.yokassaTransaction">
        <b-col
          v-if="card.wallet"
          lg="6"
        >
          <b-card title="Часть кошельком">
            <dl class="row mb-0">
              <template v-for="(value, key) in card.wallet">
                <dt
                  :key="`wk-${key}`"
                  class="col-sm-5 text-muted"
                >
                  {{ key }}
                </dt>
                <dd
                  :key="`wv-${key}`"
                  class="col-sm-7"
                >
                  {{ value === null || value === '' ? '—' : value }}
                </dd>
              </template>
            </dl>
          </b-card>
        </b-col>
        <b-col
          v-if="card.yokassaTransaction"
          lg="6"
        >
          <b-card title="Запись о запросе в ЮKassa">
            <dl class="row mb-0">
              <template v-for="(value, key) in card.yokassaTransaction">
                <dt
                  :key="`yk-${key}`"
                  class="col-sm-5 text-muted"
                >
                  {{ key }}
                </dt>
                <dd
                  :key="`yv-${key}`"
                  class="col-sm-7"
                >
                  {{ value === null || value === '' ? '—' : value }}
                </dd>
              </template>
            </dl>
          </b-card>
        </b-col>
      </b-row>

      <b-card title="Журнал платежа">
        <payment-events-table
          :rows="card.events"
          :count="card.events.length"
          :per-page="Math.max(card.events.length, 1)"
        />
      </b-card>
    </div>

    <b-modal
      v-model="linkModal"
      title="Привязать платёж"
      ok-title="Привязать"
      cancel-title="Отмена"
      :ok-disabled="!linkForm.entityId || acting"
      @ok.prevent="link"
    >
      <p class="text-muted">
        Платёж этого типа привязывается только к записи вида «{{ entityLabels[linkForm.entityType] || '—' }}».
      </p>
      <b-form-group :label="`Номер записи («${entityLabels[linkForm.entityType] || ''}»)`">
        <b-form-input
          v-model.number="linkForm.entityId"
          type="number"
          min="1"
          autofocus
        />
      </b-form-group>
      <b-form-checkbox
        v-if="payment && payment.entityType"
        v-model="linkForm.replace"
      >
        Заменить текущую привязку ({{ entityLabels[payment.entityType] }} #{{ payment.entityId }})
      </b-form-checkbox>
      <b-alert
        v-if="linkError"
        show
        variant="danger"
        class="mt-1 mb-0"
      >
        <div class="alert-body">
          {{ linkError }}
        </div>
      </b-alert>
    </b-modal>
  </div>
</template>

<script>
import {
  BAlert,
  BBadge,
  BButton,
  BCard,
  BCol,
  BFormCheckbox,
  BFormGroup,
  BFormInput,
  BModal,
  BRow,
  BSpinner,
  BTable,
} from 'bootstrap-vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import paymentAdminApi from '@/services/payment-admin-api.service'
import PaymentFindings from './components/PaymentFindings.vue'
import PaymentEventsTable from './components/PaymentEventsTable.vue'
import {
  ENTITY_LABELS, LINKABLE_ENTITY, TYPE_LABELS, errorMessage, formatDate, formatMoney, statusVariant,
} from './payment-labels'

export default {
  name: 'PaymentPage',
  components: {
    BAlert,
    BBadge,
    BButton,
    BCard,
    BCol,
    BFormCheckbox,
    BFormGroup,
    BFormInput,
    BModal,
    BRow,
    BSpinner,
    BTable,
    PaymentFindings,
    PaymentEventsTable,
  },
  data() {
    return {
      card: null,
      loading: false,
      acting: false,
      errorText: '',
      applyResult: null,
      linkModal: false,
      linkError: '',
      linkForm: { entityType: '', entityId: null, replace: false },
      typeLabels: TYPE_LABELS,
      entityLabels: ENTITY_LABELS,
      attemptFields: [
        { key: 'id', label: 'Попытка' },
        { key: 'status', label: 'Статус' },
        { key: 'amountKopecks', label: 'Сумма' },
        { key: 'createdAt', label: 'Создана' },
        { key: 'processedAt', label: 'Применена' },
        { key: 'superseded', label: '' },
      ],
    }
  },
  computed: {
    payment() {
      return this.card && this.card.payment
    },
    paymentId() {
      return Number(this.$route.params.id)
    },
  },
  watch: {
    paymentId() {
      this.card = null
      this.applyResult = null
      this.load(false)
    },
  },
  created() {
    this.load(false)
  },
  methods: {
    formatDate,
    formatMoney,
    statusVariant,
    attemptClass(item) {
      return item && item.isThis ? 'table-active' : ''
    },
    async load(live) {
      this.loading = true
      this.errorText = ''
      try {
        const { data } = await paymentAdminApi.getPayment(this.paymentId, live)
        this.card = data
      } catch (e) {
        this.errorText = errorMessage(e)
      } finally {
        this.loading = false
      }
    },
    toast(title, variant = 'success', icon = 'CheckIcon') {
      this.$toast({ component: ToastificationContent, props: { title, icon, variant } })
    },
    onAction(action) {
      if (action === 'recheck') this.recheck()
      else if (action === 'reapply') this.reapply()
      else if (action === 'link_entity') this.openLink()
    },
    async recheck() {
      this.acting = true
      try {
        const { data } = await paymentAdminApi.recheck(this.paymentId)
        this.toast(data.changed ? `Статус обновлён: ${data.previousStatus || 'нет'} → ${data.status}` : `Статус не изменился (${data.status})`)
        await this.load(true)
      } catch (e) {
        this.toast(errorMessage(e), 'danger', 'AlertCircleIcon')
      } finally {
        this.acting = false
      }
    },
    async reapply() {
      const confirmed = await this.$bvModal.msgBoxConfirm(
        'Платёж будет применён так же, как при уведомлении от ЮKassa: регистрация или заказ отметятся оплаченными. Если они уже оплачены другой попыткой, применение будет отклонено.',
        {
          title: 'Применить платёж?',
          okTitle: 'Применить',
          cancelTitle: 'Отмена',
          footerClass: 'p-2',
          centered: true,
        },
      )
      if (!confirmed) return

      this.acting = true
      try {
        const { data } = await paymentAdminApi.reapply(this.paymentId)
        this.applyResult = data
        this.card = data.card
      } catch (e) {
        // a refusal (already paid, still being applied, not paid at the provider…) is an answer, not a failure
        this.toast(errorMessage(e), 'danger', 'AlertCircleIcon')
      } finally {
        this.acting = false
      }
    },
    openLink() {
      this.linkError = ''
      this.linkForm = {
        entityType: LINKABLE_ENTITY[this.payment.type] || '',
        entityId: null,
        replace: false,
      }
      this.linkModal = true
    },
    async link() {
      this.acting = true
      this.linkError = ''
      try {
        const { data } = await paymentAdminApi.linkEntity(this.paymentId, {
          entityType: this.linkForm.entityType,
          entityId: this.linkForm.entityId,
          ...(this.linkForm.replace ? { replace: true } : {}),
        })
        this.card = data
        this.linkModal = false
        this.toast('Платёж привязан')
      } catch (e) {
        this.linkError = errorMessage(e)
      } finally {
        this.acting = false
      }
    },
  },
}
</script>
